import './ListSearch.css';

// S12a — THE SEARCH BOX, as an instrument of its own.
//
// The Clients screen used to point at ⌘K to find anyone outside its slice.
// ⌘K opens the CAPTURE sheet, and the Knot's classifier carries only CREATE
// targets — asking it to find a client produces a note. So the promise was
// never keepable, and Huan's ruling (2026-08-12) is that search is its own
// instrument until the Knot can actually find things.
//
// It is therefore a VISIBLE, TAPPABLE control sitting on the screen, not a
// keystroke: on a phone there is no ⌘, and the office's own machine is not
// where the shop lives. Nothing here binds a shortcut — wiring ⌘K to this
// box is a separate piece of work, and it can only happen after the Knot
// grows a find intent.
//
// It filters what the screen has already loaded. No request, no debounce,
// no spinner: the answer is in memory and it should feel like it.

export interface ListSearchProps {
  value: string;
  onChange: (next: string) => void;
  /** What this box searches, in the screen's own words. */
  placeholder: string;
  /** The accessible name — what a screen reader announces. */
  label: string;
}

export function ListSearch({ value, onChange, placeholder, label }: ListSearchProps) {
  return (
    <div class="list-search">
      <span class="list-search__glyph" aria-hidden="true">
        ⌕
      </span>
      <input
        class="list-search__input"
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        autocomplete="off"
        autocorrect="off"
        spellcheck={false}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
      />
      {/* The clear is a real target, not a keystroke: Escape works too on a
          keyboard, but a thumb must be able to undo the filter. */}
      {value.length > 0 ? (
        <button
          type="button"
          class="list-search__clear"
          aria-label="Clear the search"
          onClick={() => onChange('')}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

/** Fold a haystack for matching: case- and accent-insensitive, so typing
 *  "fortinos" finds "Fortinos" and typing "montreal" finds "Montréal". */
export function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Every space-separated term must appear somewhere in the row's own text —
 *  so "fortinos freezer" narrows rather than widening, and the order the
 *  words were typed in does not matter. An empty query matches everything. */
export function matches(query: string, haystacks: readonly (string | null | undefined)[]): boolean {
  const terms = fold(query).split(/\s+/).filter((t) => t.length > 0);
  if (terms.length === 0) return true;
  const hay = fold(haystacks.filter((h): h is string => typeof h === 'string' && h.length > 0).join('   '));
  return terms.every((t) => hay.includes(t));
}
